import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product discount percentage.
 * This component listens for variant update events and updates the discount display accordingly.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} discountText - The element that displays the discount percentage text
 *
 * @extends {Component<Refs>}
 */
class ProductDiscount extends Component {
  requiredRefs = ['discountText'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateDiscount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateDiscount);
  }

  /**
   * Updates the discount percentage display.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateDiscount = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;
    if (!variant) return;

    const compareAtPrice = variant.compare_at_price;
    const price = variant.price;

    if (compareAtPrice && compareAtPrice > price) {
      const percentage = Math.floor(((compareAtPrice - price) * 100) / compareAtPrice);
      this.style.display = 'block';
      this.refs.discountText.textContent = `${percentage}% Off`;
    } else {
      this.style.display = 'none';
      this.refs.discountText.textContent = '';
    }
  };
}

if (!customElements.get('product-discount')) {
  customElements.define('product-discount', ProductDiscount);
}
